import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Button,
  Checkbox,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  App,
  Spin,
  Collapse,
  Typography,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  DeleteOutlined,
  SendOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import WarehouseCorrectionPanel from '../modules/warehouse/WarehouseCorrectionPanel';
import { saveWZCorrection } from '../modules/warehouse/warehouseCorrectionApi';
import { confirmWZQuantityWarning } from '../modules/warehouse/wzReleaseFlow';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  extractWarehouseQuantitySetPatch,
  formatWarehouseQuantity,
  getWarehouseLineUnit,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatConversionValue } from '../utils/dimensionConversion';
import {
  WZItem,
  WZStatus,
  CreateWZDto,
  UpdateWZDto
} from '../types/wz';
import {
  WZDocumentDto,
  attachWZItemDimensions,
  buildRecipientFieldsFromCustomer,
  normalizeWZDocument,
  prepareWZItemsForSave
} from '../dto/wz';
import { OrderOptionDto, normalizeOrderOptions } from '../dto/order';
import { canManageWZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getEditableWZStatusOptions, isWZEditable } from '../utils/wzEditRules';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import CustomerNameCombobox from '../components/inventory/CustomerNameCombobox';
import { buildWzVariantAutofill } from '../utils/wzVariantSelection';
import type { ProductVariant } from '../types/pz';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import type { TransportRouteSummaryDto } from '../dto/transport';
import { mergeWZTransportOptions } from '../utils/wzTransportSelection';
import WZTransportSummary from '../modules/wz/components/WZTransportSummary';
import WZBatchConversionMenu from '../modules/wz/components/WZBatchConversionMenu';
import {
  convertWZItemsToTarget,
  type WZBatchConversionTarget,
} from '../modules/wz/wzBatchQuantityConversion';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import { WZ_QUANTITY_MAX } from '../utils/warehouseQuantity';
import { getWZErrorMessage, isBlockingWZError } from '../modules/warehouse/wzErrorMessages';
import { askWarehouseSaveIntent } from '../modules/warehouse/warehouseSaveIntent';
import { releaseWZDocument } from '../modules/warehouse/wzReleaseFlow';
import { useFeedback } from '../utils/feedback';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { formatCurrency } from '../utils/currency';
import { formatUnit } from '../utils/units';
import './WZItemsView.css';
import './WZDetail.css';

const { Option } = Select;
const { TextArea } = Input;
const createEmptyItem = (): Partial<WZItem> => ({ productName: '', quantity: 1, unit: 'pcs' });

const formatLineDimensions = (item: Partial<WZItem>) =>
  formatProductDimensions({
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
  }) || '-';

const WZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['wz', 'common', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();

  const isNewDocument = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const requestedEditMode = queryParams.get('mode') === 'edit' || isNewDocument;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wzDocument, setWzDocument] = useState<WZDocumentDto | null>(null);
  const [items, setItems] = useState<Partial<WZItem>[]>([createEmptyItem()]);
  const [orders, setOrders] = useState<OrderOptionDto[]>([]);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [linkedTransports, setLinkedTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canViewDocumentSalePrice = authService.canViewWZDocumentSalePrices();
  const canEditDocumentSalePrice = authService.canEditWZDocumentSalePrices();
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const currentUserDisplayName = currentUser?.name || '';
  const canEditDocument = canEdit && (isNewDocument || isWZEditable(wzDocument?.status));
  const isEditMode = requestedEditMode && canEditDocument;
  const selectedOrderId = Form.useWatch('orderId', form);
  const backgroundRefreshAllowed = !isNewDocument && !requestedEditMode && !saving;
  const backgroundRefreshAllowedRef = useRef(backgroundRefreshAllowed);
  backgroundRefreshAllowedRef.current = backgroundRefreshAllowed;

  useEffect(() => {
    if (!isNewDocument) {
      fetchWZDocument();
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const orderIdForTransports = String(selectedOrderId || '').trim();

    if (!orderIdForTransports) {
      setTransports(linkedTransports);
      return () => { cancelled = true; };
    }

    const fetchTransports = async () => {
      try {
        const result = await fetchOrderTransports(orderIdForTransports);
        if (!cancelled) {
          setTransports(mergeWZTransportOptions(result.transports, linkedTransports));
        }
      } catch (error: any) {
        console.error('Error fetching order transports:', error);
        if (!cancelled) {
          setTransports(linkedTransports);
        }
      }
    };

    fetchTransports();
    return () => { cancelled = true; };
  }, [selectedOrderId, linkedTransports]);

  const fetchWZDocument = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const response = await api.get(`/wz/${id}`);
      if (background && !backgroundRefreshAllowedRef.current) return;
      const doc = normalizeWZDocument(response.data, 'wz.detail.document');
      setWzDocument(doc);

      // Set form values
      form.setFieldsValue({
        correctionReason: undefined,
        ...doc,
        receivedBy: doc.receivedBy?.trim() || t('transport:driver'),
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : undefined,
        releaseDate: doc.releaseDate ? dayjs(doc.releaseDate) : undefined,
        receivedDate: doc.receivedDate ? dayjs(doc.receivedDate) : undefined,
        orderId: doc.orderId,
        transportIds: doc.transportIds || [],
      });
      const docLinkedTransports = Array.isArray(doc.linkedTransports) ? doc.linkedTransports : [];
      setLinkedTransports(docLinkedTransports);
      setTransports((previous) => mergeWZTransportOptions(previous, docLinkedTransports));

      // The delivery address input only shows when it actually diverges from
      // the recipient address — a WZ saved with delivery == recipient opens
      // with the toggle off (backend already treats empty deliveryAddress as
      // "use recipientAddress", see handleSave below).
      const loadedDeliveryAddress = (doc.deliveryAddress || '').trim();
      const loadedRecipientAddress = (doc.recipientAddress || '').trim();
      setDeliveryDifferent(loadedDeliveryAddress !== '' && loadedDeliveryAddress !== loadedRecipientAddress);

      // Attach the linked order's product dimensions so the per-line m²/m³
      // conversion can be shown/recomputed in the form (mirrors PDF resolution).
      setItems(doc.itemsLoaded
        ? attachWZItemDimensions(doc.items, doc.orderProducts)
        : [createEmptyItem()]);
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.fetchFailed'));
      navigate('/app/wz');
    } finally {
      if (!background) setLoading(false);
    }
  };

  usePageAutoRefresh(() => fetchWZDocument(true), { enabled: backgroundRefreshAllowed });

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(normalizeOrderOptions(response.data, 'wz.detail.orders'));
    } catch (error: any) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleOrderChange = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!wzDocument || orderId !== wzDocument.orderId) {
      setLinkedTransports([]);
    }
    form.setFieldsValue({
      transportIds: [],
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    });

    if (order) {
      form.setFieldsValue({
        recipientName: order.customer?.name || '',
        recipientAddress: order.customer?.address || '',
        recipientPhone: order.customer?.phone || '',
        recipientEmail: order.customer?.email || '',
      });

      // Deliberately no item seeding here: handleOrderChange only fires
      // when `isNewDocument` (id === 'new'), but App.tsx routes `wz/new` to
      // WZNew.tsx exclusively — `wz/:id` (this page) never receives the
      // literal id "new" — so this branch of the form is unreachable through
      // normal navigation. Order-driven WZ item seeding has exactly one live
      // home: the WZ create domain seam, which reads
      // GET /wz/prefill/:orderId for the remaining-quantity-netted,
      // variant-linked item list. See frontend/AGENTS.md.
    }
  };

  const isCorrection = wzDocument?.status === WZStatus.RELEASED || wzDocument?.status === WZStatus.RECEIVED;

  const handleSave = async (values: any) => {
    if (!canEditDocument) {
      feedback.error(t('wz:error.saveForbidden'), { sticky: true });
      return;
    }

    if (!isNewDocument && wzDocument && !wzDocument.itemsLoaded) {
      feedback.error(t('wz:error.itemsUnavailable'), { sticky: true });
      return;
    }

    // Saving a WZ is a two-part decision (save + move the stock?), so it is
    // asked once, here, instead of sending the operator back into the saved
    // document for a second click. See modules/warehouse/warehouseSaveIntent.
    const saveIntent = isCorrection ? 'DRAFT' : await askWarehouseSaveIntent({ documentType: 'WZ', modal, t });
    if (!saveIntent) {
      return;
    }

    setSaving(true);
    try {
      const safeItems = prepareWZItemsForSave(items).map((item) => {
        const { totalValue, notes, ...safeItem } = item;
        return safeItem;
      });
      const data: CreateWZDto | UpdateWZDto = {
        ...values,
        ...(isCorrection ? { expectedUpdatedAt: wzDocument?.updatedAt } : {}),
        wzNumber: values.wzNumber?.trim() || undefined,
        issueDate: values.issueDate?.format('YYYY-MM-DD'),
        releaseDate: wzDocument?.releaseDate || values.issueDate?.format('YYYY-MM-DD'),
        receivedDate: values.receivedDate ? values.receivedDate.format('YYYY-MM-DD') : null,
        transportIds: transports
          .filter((transport) => transport.status !== 'CANCELLED')
          .map((transport) => transport.id),
        items: safeItems,
        // The delivery input is hidden (and its stored value stale) whenever
        // the "different than registered" toggle is off — force it empty so
        // the backend falls back to recipientAddress instead of persisting a
        // leftover value from before the toggle was switched off.
        deliveryAddress: deliveryDifferent ? (values.deliveryAddress || '') : '',
      };
      delete data.status;
      delete (data as { costOfGoodsSold?: number | null }).costOfGoodsSold;
      delete data.producerInvoice;
      delete data.purchaseInvoiceAmount;
      delete data.transportPrice;
      delete data.transportInvoiceNo;
      delete data.transportCompany;
      delete data.driverName;
      delete data.transportNotes;

      if (isNewDocument) {
        const response = await api.post('/wz', data);
        // Server-assigned when the operator left the number blank.
        const createdNumber = response?.data?.wzNumber || data.wzNumber;
        feedback.success(createdNumber
          ? t('wz:success.created', { wzNumber: createdNumber })
          : t('wz:success.createdFallback'));
        const createdId = response?.data?.id;
        if (saveIntent === 'POST' && createdId) {
          await releaseWZDocument({
            wzId: createdId,
            wzNumber: createdNumber,
            modal,
            t,
            language: i18n.language,
            feedback,
          });
        }
        navigate(createdId ? `/app/wz/${createdId}` : '/app/wz');
      } else {
        if (isCorrection && id) {
          const saved = await saveWZCorrection(id, data as unknown as Record<string, unknown>,
            warning => confirmWZQuantityWarning(warning, modal, t, i18n.language));
          if (!saved) return;
        } else {
          await api.put(`/wz/${id}`, data);
        }
        const savedNumber = data.wzNumber || wzDocument?.wzNumber;
        feedback.success(savedNumber
          ? t('wz:success.updated', { wzNumber: savedNumber })
          : t('wz:success.updatedFallback'));
        if (saveIntent === 'POST' && id) {
          // A failed release leaves the document saved as a draft and says why
          // — the operator fixes it and retries from "Rozlicz wydanie".
          await releaseWZDocument({
            wzId: id,
            wzNumber: savedNumber,
            modal,
            t,
            language: i18n.language,
            feedback,
          });
        }
        // Stay on the document: drop ?mode=edit (back to view mode) and refresh.
        navigate(`/app/wz/${id}`, { replace: true });
        fetchWZDocument();
      }
    } catch (error: any) {
      // Code -> Polish sentence now lives in modules/warehouse/wzErrorMessages.ts
      // so the list page and the create wizard read identically.
      feedback.error(getWZErrorMessage(error, t, 'wz:error.saveFailed'), {
        sticky: isBlockingWZError(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: WZStatus) => {
    // The release path (including the over-delivery handshake) lives in
    // modules/warehouse/wzReleaseFlow so this button and the save-time choice
    // stay one behavior.
    if (newStatus === WZStatus.RELEASED) {
      if (!id) return;
      const released = await releaseWZDocument({
        wzId: id,
        wzNumber: wzDocument?.wzNumber,
        modal,
        t,
        language: i18n.language,
        feedback,
      });
      if (released) {
        fetchWZDocument();
      }
      return;
    }

    try {
      await api.put(`/wz/${id}/status`, { status: newStatus });
      feedback.success(wzDocument?.wzNumber
        ? t('wz:success.statusUpdated', { wzNumber: wzDocument.wzNumber })
        : t('wz:success.statusUpdatedFallback'));
      fetchWZDocument();
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.statusUpdateFailed'), {
        sticky: isBlockingWZError(error),
      });
    }
  };

  const confirmWZRelease = () => {
    modal.confirm({
      title: t('wz:confirm.releaseTitle'),
      content: t('wz:confirm.releaseDescription'),
      okText: t('wz:actions.settleRelease'),
      cancelText: t('common:cancel'),
      onOk: () => handleStatusChange(WZStatus.RELEASED),
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzDocument?.wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('wz:success.pdfDownloaded', { wzNumber: wzDocument?.wzNumber }));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  /** Quantity, its transaction unit, and conversionUnit are decided together
   *  in the conversion modal, so they must land in one state update. */
  const updateItemQuantity = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return {
          ...item,
          ...extractWarehouseQuantitySetPatch(patch),
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          conversionQuantity: patch.conversionQuantity,
          ...(patch.unit ? { unit: patch.unit } : {}),
          saleQuantity: patch.pricingQuantity ?? null,
          saleUnit: patch.pricingUnit ?? null,
      };
    }));
  };

  const updateItemUnitPrice = (index: number, unitPrice: number | null) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, unitPrice: unitPrice ?? undefined } : item
    )));
  };

  const handleBatchConversion = (target: WZBatchConversionTarget) => {
    const result = convertWZItemsToTarget(items, target);
    setItems(result.items);

    if (result.convertedCount === 0) {
      feedback.warning(t('wz:createForm.batchConversionNone'));
      return;
    }
    if (result.skippedCount > 0) {
      feedback.warning(t('wz:createForm.batchConversionPartial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
      return;
    }
    feedback.success(t('wz:createForm.batchConversionSuccess', { count: result.convertedCount }));
  };

  /** Autofill a WZ row from a catalog variant selection. */
  const handleVariantSelect = (index: number, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      ...buildWzVariantAutofill(updatedItems[index], variantId, variant),
    };
    setItems(updatedItems);
  };

  /** Free-text edit of the product name combobox detaches any catalog link —
   *  the operator is describing a product not in the catalog (or has edited
   *  away from a previously picked one). A catalog pick right after this
   *  (handleVariantSelect, via onSelectVariant) fires as a separate event and
   *  supersedes it with the full autofill, so the two can't race. */
  const handleProductNameTyped = (index: number, productName: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], productName, productVariantId: undefined };
    setItems(updatedItems);
  };

  const statusTransitionOptions = wzDocument
    ? getEditableWZStatusOptions(wzDocument.status).filter(status => status !== wzDocument.status)
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="wz-detail-page">
      <header className="wz-detail-page__header">
        <div className="wz-detail-page__heading">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/wz')}
            aria-label={t('common:back')}
          />
          <div>
            <Typography.Text type="secondary">{t('wz:title')}</Typography.Text>
            <Typography.Title level={2}>
              {isNewDocument ? t('wz:actions.create') : wzDocument?.wzNumber}
            </Typography.Title>
          </div>
        </div>
        <Space wrap>
          {!isNewDocument && canDownloadWZPdf && (
            <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
              {t('wz:actions.downloadPDF')}
            </Button>
          )}
          {!isNewDocument && !isEditMode && canEditDocument && (
            <Button type="primary" onClick={() => navigate(`/app/wz/${id}?mode=edit`)}>
              {t('common:edit')}
            </Button>
          )}
        </Space>
      </header>

      {wzDocument?.pendingCostMarker?.costPending && <Alert type="warning" showIcon message={t('wz:pendingPurchaseCost')} style={{ marginBottom: 16 }} />}
      <Form
        form={form}
        layout="vertical"
        className="wz-detail-page__form"
        onFinish={handleSave}
        onFinishFailed={() => feedback.warning(t('wz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          issuedBy: currentUserDisplayName,
          receivedBy: t('transport:driver'),
        }}
      >
        {isCorrection && id && <WarehouseCorrectionPanel documentId={id} documentType="WZ"
          editing={isEditMode} version={wzDocument?.updatedAt} />}
        <div className="wz-detail-page__surface">
          <section className="wz-detail-section">
            <div className="wz-detail-section__heading">
              <Typography.Title level={4}>{t('wz:sections.documentAndDelivery')}</Typography.Title>
            </div>
            <Row gutter={[16, 0]}>
              {!isNewDocument && (
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    label={t('wz:fields.wzNumber')}
                    name="wzNumber"
                    rules={[
                      { required: true, message: t('wz:validation.wzNumberRequired') },
                      { max: 50, message: t('wz:validation.wzNumberMax') },
                    ]}
                  >
                    <Input disabled={!isEditMode} maxLength={50} />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label={t('wz:fields.order')}
                  name="orderId"
                >
                  <Select
                    placeholder={t('wz:placeholders.selectOrder')}
                    disabled={!isEditMode}
                    onChange={handleOrderChange}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {orders.map(order => (
                      <Option key={order.id} value={order.id}>
                        {order.label || `${order.displayOrderNumber} - ${order.customerName || '-'}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label={t('wz:fields.issueDate')}
                  name="issueDate"
                  rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label={t('wz:fields.deliveryDate')}
                  name="receivedDate"
                  rules={[{ required: true, message: t('wz:validation.deliveryDateRequired') }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={t('wz:fields.salesInvoiceNumber')} name="salesInvoiceNumber">
                  <Input
                    disabled={!isEditMode}
                    maxLength={100}
                    placeholder={t('wz:placeholders.salesInvoiceNumber')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('wz:fields.recipientName')}
                  name="recipientName"
                  rules={[{ required: true, message: t('wz:validation.recipientNameRequired') }]}
                >
                  <CustomerNameCombobox
                    onSelectCustomer={(customer) => form.setFieldsValue(buildRecipientFieldsFromCustomer(customer))}
                    disabled={!isEditMode}
                    placeholder={t('wz:placeholders.selectCustomer')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('wz:fields.recipientAddress')}
                  name="recipientAddress"
                  rules={[{ required: true, message: t('wz:validation.recipientAddressRequired') }]}
                >
                  <Input disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <div className="wz-detail-page__delivery-toggle">
                  <Checkbox
                    checked={deliveryDifferent}
                    disabled={!isEditMode}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setDeliveryDifferent(checked);
                      if (!checked) form.setFieldsValue({ deliveryAddress: '' });
                    }}
                  >
                    {t('wz:delivery.differentFromRegistered')}
                  </Checkbox>
                  {deliveryDifferent && (
                    <Form.Item name="deliveryAddress">
                      <TextArea
                        rows={2}
                        disabled={!isEditMode}
                        placeholder={t('wz:placeholders.deliveryAddress')}
                        aria-label={t('wz:fields.deliveryAddress')}
                      />
                    </Form.Item>
                  )}
                </div>
              </Col>
            </Row>

            <Collapse
              ghost
              className="wz-detail-page__inline-collapse"
              items={[{
                key: 'recipient-contact',
                label: (
                  <Space size={8}>
                    <span>{t('wz:sections.recipientContact')}</span>
                    <Typography.Text type="secondary">({t('wz:transport.optionalTag')})</Typography.Text>
                  </Space>
                ),
                children: (
                  <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                      <Form.Item label={t('wz:fields.recipientNIP')} name="recipientNIP">
                        <Input disabled={!isEditMode} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={t('wz:fields.recipientPhone')} name="recipientPhone">
                        <Input disabled={!isEditMode} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={t('wz:fields.recipientEmail')} name="recipientEmail">
                        <Input type="email" disabled={!isEditMode} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              }]}
            />
          </section>

          <section className="wz-detail-section wz-items-section">
            <div className="wz-detail-section__heading wz-detail-section__heading--actions">
              <Typography.Title level={4}>{t('wz:sections.items')}</Typography.Title>
              {isEditMode && (
                <Space wrap>
                  {items.length > 1 && <WZBatchConversionMenu onConvert={handleBatchConversion} />}
                  <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
                    {t('wz:actions.addItem')}
                  </Button>
                </Space>
              )}
            </div>
            <WZItemsList compact>
              {items.map((item, index) => {
                const physicalUnit = getWarehouseLineUnit(item);
                const storedPricingUnit = item.saleUnit
                  ? getWarehouseLineUnit({ unit: item.saleUnit })
                  : null;
                const storedPricingQuantity = Number(item.saleQuantity);
                const derivedConversion = formatConversionValue(
                  item.conversionUnit,
                  { ...item, unit: physicalUnit },
                  Number(item.quantity) || 0,
                );
                const conversionLabel = storedPricingUnit
                  && storedPricingUnit !== physicalUnit
                  && Number.isFinite(storedPricingQuantity)
                  && storedPricingQuantity > 0
                  ? formatWarehouseQuantity(storedPricingQuantity, storedPricingUnit, i18n.language)
                  : derivedConversion;

                return (
                  <WZItemCard
                    compact
                    key={`${item.id || item.productVariantId || 'item'}-${index}`}
                    index={index}
                    positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                    productLabel={t('wz:fields.productName')}
                    product={isEditMode ? (
                      <ProductNameCombobox
                        value={item.productName || ''}
                        onChange={(productName) => handleProductNameTyped(index, productName)}
                        onSelectVariant={(variantId, variant) => handleVariantSelect(index, variantId, variant)}
                        placeholder={t('wz:placeholders.productName')}
                      />
                    ) : item.productName}
                    action={isEditMode ? (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                        aria-label={t('common:delete')}
                      />
                    ) : undefined}
                  >
                    <WZItemField label={t('wz:fields.dimensions')}>
                      {formatLineDimensions(item)}
                    </WZItemField>
                    <WZItemField label={t('wz:fields.quantity')} wide={isEditMode} emphasis>
                      {isEditMode ? (
                        <WarehouseQuantityField
                          line={item}
                          onChange={(patch) => updateItemQuantity(index, patch)}
                          pricingQuantity={item.saleQuantity}
                          pricingUnit={item.saleUnit}
                          maxQuantity={WZ_QUANTITY_MAX}
                          unitLabel={t('wz:fields.unit')}
                          maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                          noDimensionsLabel={t('wz:conversion.noDimensions')}
                        />
                      ) : formatWarehouseQuantity(item.quantity, item.unit, i18n.language)}
                    </WZItemField>
                    {!isEditMode && (
                      <WZItemField label={t('wz:fields.conversion')}>
                        {conversionLabel || '—'}
                      </WZItemField>
                    )}
                    {canViewDocumentSalePrice && (
                      <WZItemField label={t('wz:fields.unitPrice')} wide={isEditMode}>
                        {isEditMode && canEditDocumentSalePrice ? (
                          <InputNumber
                            min={0.0001}
                            max={99999999.99}
                            precision={4}
                            value={item.unitPrice}
                            onChange={(value) => updateItemUnitPrice(index, value)}
                            addonAfter={`${wzDocument?.currency || 'PLN'} / ${formatUnit(item.saleUnit || item.unit, i18n.language)}`}
                            placeholder={t('wz:placeholders.unitPrice')}
                            style={{ width: '100%' }}
                          />
                        ) : item.unitPrice != null ? (
                          `${formatCurrency(item.unitPrice, wzDocument?.currency || 'PLN')} / ${formatUnit(item.saleUnit || item.unit, i18n.language)}`
                        ) : '—'}
                      </WZItemField>
                    )}
                  </WZItemCard>
                );
              })}
            </WZItemsList>
          </section>

          <section className="wz-detail-section">
            <div className="wz-detail-section__heading">
              <Typography.Title level={4}>{t('wz:createForm.releaseSection')}</Typography.Title>
            </div>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t('wz:fields.issuedBy')}
                  name="issuedBy"
                  rules={[
                    { required: true, message: t('wz:validation.issuedByRequired') },
                    { max: 100, message: t('wz:validation.personNameMax') },
                  ]}
                >
                  <Input disabled={!isEditMode} placeholder={t('wz:placeholders.issuedBy')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={t('wz:fields.vehicleRegistration')} name="vehicleRegistration">
                  <Input disabled={!isEditMode} placeholder={t('wz:placeholders.vehicleRegistration')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t('wz:fields.receivedBy')}
                  name="receivedBy"
                  rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                >
                  <Input disabled={!isEditMode} placeholder={t('wz:placeholders.receivedBy')} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={t('wz:fields.notes')} name="notes">
                  <TextArea rows={3} disabled={!isEditMode} placeholder={t('wz:placeholders.notes')} />
                </Form.Item>
              </Col>
            </Row>
          </section>

          <section className="wz-detail-section wz-detail-section--collapsed">
            <Collapse
              ghost
              items={[{
                key: 'transport',
                label: (
                  <Space size={8}>
                    <span>{t('wz:sections.transport')}</span>
                    <Typography.Text type="secondary">({t('wz:transport.optionalTag')})</Typography.Text>
                  </Space>
                ),
                children: (
                  <>
                    <Typography.Paragraph type="secondary">
                      {t('wz:transport.autofillHint')}
                    </Typography.Paragraph>
                    <WZTransportSummary transports={transports} />
                  </>
                ),
              }]}
            />
          </section>
        </div>

        {isEditMode && (
          <div className="wz-detail-page__sticky-actions">
            <Button onClick={() => navigate('/app/wz')}>{t('common:cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {t('common:save')}
            </Button>
          </div>
        )}
      </Form>

      {!isNewDocument && !isEditMode && wzDocument && canEdit && statusTransitionOptions.length > 0 && (
        <section className="wz-detail-page__secondary-surface">
          <Collapse
            ghost
            items={[{
              key: 'warehouse-settlement',
              label: t('wz:sections.warehouseSettlement'),
              children: (
                <>
                  <Typography.Paragraph type="secondary">
                    {t('wz:operations.sectionHint')}
                  </Typography.Paragraph>
                  <Space wrap>
                    {statusTransitionOptions.includes(WZStatus.RELEASED) && (
                      <Space size={6}>
                        <Button icon={<SendOutlined />} onClick={confirmWZRelease}>
                          {t('wz:actions.settleRelease')}
                        </Button>
                        <Tooltip title={t('wz:operations.releaseInventoryEffect')}>
                          <InfoCircleOutlined
                            tabIndex={0}
                            aria-label={t('wz:operations.releaseInventoryEffect')}
                            className="wz-detail-page__help-icon"
                          />
                        </Tooltip>
                      </Space>
                    )}
                    {statusTransitionOptions.includes(WZStatus.CANCELLED) && (
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleStatusChange(WZStatus.CANCELLED)}
                      >
                        {t('wz:actions.cancel')}
                      </Button>
                    )}
                  </Space>
                </>
              ),
            }]}
          />
        </section>
      )}
    </div>
  );
};

export default WZDetail;
